import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiXCircle, FiAlertTriangle, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';
import ManageHeader from './ManageHeader';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

export default function CancelFlow({ token, bookingData, refreshBooking }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const { booking, merchant } = bookingData;

  const [step, setStep] = useState(() => {
    if (booking.booking_state === 'cancelled') return 'already-cancelled';
    if (new Date() >= new Date(booking.start_date)) return 'already-started';
    return 'confirm';
  });
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState(null);
  const [refundInfo, setRefundInfo] = useState(null);

  const policy = merchant?.cancellation_policy || {};
  const hoursBeforeStart = policy.hours_before_start ?? 0;
  const refundPercentage = policy.refund_percentage ?? 0;

  const { hoursUntilStart, withinWindow, effectiveRefundPct, estimatedRefund } = useMemo(() => {
    const now = new Date();
    const start = new Date(booking.start_date);
    const hours = (start - now) / (1000 * 60 * 60);
    const inWindow = hours >= hoursBeforeStart;
    const pct = inWindow ? refundPercentage : 0;
    const refund = parseFloat(booking.amount_paid || 0) * (pct / 100);
    return { hoursUntilStart: hours, withinWindow: inWindow, effectiveRefundPct: pct, estimatedRefund: refund };
  }, [booking.start_date, booking.amount_paid, hoursBeforeStart, refundPercentage]);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/manage/cancel-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason,
          updatedAt: booking.updated_at
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err.code === 'ALREADY_CANCELLED') {
          setStep('already-cancelled');
          return;
        }
        if (err.code === 'ALREADY_STARTED') {
          setStep('already-started');
          return;
        }
        throw new Error(err.error || 'Failed to cancel booking');
      }

      const data = await res.json();
      setRefundInfo(data.refund);
      setStep('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleDone = async () => {
    await refreshBooking();
    navigate(`/b/${token}`);
  };

  if (step === 'already-cancelled') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-yellow-50">
              <SafeIcon icon={FiAlertCircle} className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.alreadyCancelled', 'Booking Already Cancelled')}
            </h1>
            <p className="text-gray-500 mb-8">
              {t('manage.alreadyCancelledDesc', 'This booking has already been cancelled.')}
            </p>
            <button
              onClick={handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  if (step === 'already-started') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-50">
              <SafeIcon icon={FiAlertCircle} className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.alreadyStarted', 'Rental Already Started')}
            </h1>
            <p className="text-gray-500 mb-8">
              {t('manage.alreadyStartedDesc', 'This booking can no longer be cancelled because the rental has already started.')}
            </p>
            <button
              onClick={handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManageHeader merchant={merchant} token={token} />
        <main id="main-content" className="max-w-xl mx-auto px-4 py-12 text-center">
          <m.div {...motion.scaleIn}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-50">
              <SafeIcon icon={FiCheck} className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('manage.bookingCancelled', 'Booking Cancelled')}
            </h1>
            <p className="text-gray-500 mb-4">
              {t('manage.cancelSuccessDesc', 'Your booking has been cancelled. A confirmation email has been sent.')}
            </p>
            {refundInfo && refundInfo.eligible && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
                <p className="text-green-800 text-sm font-medium">
                  {t('manage.refundEligible', 'You are eligible for a {{percentage}}% refund.', { percentage: refundInfo.percentage })}
                </p>
                <p className="text-green-600 text-xs mt-1">
                  {t('manage.refundNote', 'The refund will appear on your statement within 5-10 business days.')}
                </p>
              </div>
            )}
            <button
              onClick={handleDone}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-colors"
              style={{ backgroundColor: theme.colors.primary.base }}
            >
              {t('manage.backToBooking', 'Back to Booking')}
            </button>
          </m.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ManageHeader merchant={merchant} token={token} showBack />

      <main id="main-content" className="max-w-xl mx-auto px-4 py-6 space-y-6">
        <m.div {...motion.fadeInUp}>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {t('manage.cancelBooking', 'Cancel Booking')}
          </h1>
          <p className="text-gray-500 text-sm">
            {t('manage.cancelDesc', 'Please review the cancellation policy before proceeding.')}
          </p>
        </m.div>

        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Cancellation Policy */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-500" />
            {t('manage.cancellationPolicy', 'Cancellation Policy')}
          </h2>

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('manage.cancellationWindow', 'Free cancellation before')}</span>
              <span className="font-medium text-gray-900">
                {hoursBeforeStart} {t('manage.hoursBeforeStart', 'hours before start')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('manage.refundAmount', 'Refund')}</span>
              <span className="font-medium text-gray-900">{refundPercentage}%</span>
            </div>
          </div>

          {withinWindow ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-800 text-sm font-medium">
                {t('manage.eligibleForRefund', 'You are eligible for a refund.')}
              </p>
              <p className="text-green-600 text-xs mt-1">
                {t('manage.refundPercentageInfo', 'Refund: {{percentage}}%', { percentage: effectiveRefundPct })}
              </p>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm font-medium">
                {t('manage.noRefund', 'The cancellation window has passed.')}
              </p>
              <p className="text-red-600 text-xs mt-1">
                {t('manage.noRefundDesc', 'Cancellations at this point are non-refundable.')}
              </p>
            </div>
          )}
        </m.div>

        {/* Reason */}
        <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-2">
            {t('manage.cancelReason', 'Reason for cancellation (optional)')}
          </label>
          <textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border border-gray-300 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
            placeholder={t('manage.cancelReasonPlaceholder', 'Tell us why you are cancelling...')}
          />
        </m.div>

        {/* Actions */}
        <m.div {...motion.fadeInUp} className="flex gap-3">
          <button
            onClick={() => navigate(`/b/${token}`)}
            className="flex-1 py-4 rounded-xl font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('manage.keepBooking', 'Keep Booking')}
          </button>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="flex-1 py-4 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {cancelling
              ? t('manage.cancelling', 'Cancelling...')
              : t('manage.confirmCancel', 'Cancel Booking')}
          </button>
        </m.div>
      </main>
    </div>
  );
}
